import React, { useEffect, useRef } from "react";
import { FlatFileExplorerProps } from "./FlatFileExplorer";
import {
  FileLoadEventArgs,
  FileManagerComponent,
  FileManagerModel,
  DetailsViewSettingsModel,
  FileOpenEventArgs,
  FileSelectEventArgs,
} from "@syncfusion/ej2-react-filemanager";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import {
  FlatFileData,
  FlatFileManagerSettings,
  FileExplorerGridColumn,
  FileExplorerGridColumns,
} from "./types";
import { isNil, orderBy } from "lodash";
import { library, icon } from "@fortawesome/fontawesome-svg-core";
import {
  faThumbtack,
  faThumbtackSlash,
} from "@fortawesome/free-solid-svg-icons";

export const useFlatFileExplorer = <T>(
  props: FlatFileExplorerProps<T>,
  setFilesData: (data: { [key: string]: Object }[]) => void,
  setRenderKey: (key: string) => void,
  setSelectedData?: (data: T | undefined) => void
) => {
  const [ready, setReady] = React.useState<boolean>(false);
  const createdRef = React.useRef<boolean>(false);
  const flatFileSettingsRef = useRef<FlatFileManagerSettings<T> | undefined>(
    undefined
  );
  const settingsRef = useRef<
    FileManagerModel | DefaultHtmlAttributes | undefined
  >(undefined);
  const flatFileExplorerRef = useRef<FileManagerComponent>(null);
  const fieldKey = useRef<string>(
    `flat-file-explorer-${Math.random().toString(36).substring(2, 15)}`
  );
  const searchOgData = useRef<FlatFileData<T>[]>([]);

  const defaultGridColumns: FileExplorerGridColumn[] = [
    FileExplorerGridColumns.name,
    FileExplorerGridColumns.size,
    FileExplorerGridColumns.dateModified,
  ];

  const defaultFileManagerSettings: FileManagerModel | DefaultHtmlAttributes = {
    allowMultiSelection: false,
    showItemCheckBoxes: false,
    showThumbnail: false,
    toolbarSettings: {
      visible: false,
    },
    view: "Details",
    contextMenuSettings: {
      visible: false,
    },
    navigationPaneSettings: {
      visible: true,
      minWidth: "326px",
    },
    cssClass: "ucl-flat-file-explorer",
    detailsViewSettings: {
      columnResizing: false,
      columns: defaultGridColumns,
    },
  };

  useEffect(() => {
    if (props.data && ready) {
      const resolvedData = resolveData([...props.data]);
      setFilesData(resolvedData);
      searchOgData.current = resolvedData;
      setRenderKey(Math.random().toString(36).substring(2, 15));
    }
  }, [props.data]);

  useEffect(() => {
    if (!props.data) {
      return;
    }

    init([...props.data]);
    setReady(true);
  }, []);

  const searchFolderByName = (searchString: string) => {
    if (!searchString) {
      setFilesData(searchOgData.current as { [key: string]: Object }[]);
      flatFileExplorerRef.current?.refreshFiles();
      return;
    }

    // Get all folders that match the search string
    const filteredData = searchOgData.current.filter(
      (item) =>
        !item.isFile &&
        (item.name?.toLowerCase().includes(searchString.toLowerCase()) ||
          isNil(item.parentId))
    );

    // Add all files within the filtered folders
    const files = searchOgData.current.filter((item) =>
      filteredData.some((folder) => item.isFile && folder.id === item.parentId)
    );
    filteredData.push(...files);

    setFilesData(filteredData as { [key: string]: Object }[]);
    flatFileExplorerRef.current?.refreshFiles();
    flatFileExplorerRef.current?.refreshLayout();
  };

  const searchFolder = (searchString: string) => {
    // Need to go to root folder to reset view and avoid errors
    // Open file does not seems to react, will re-render inner component only
    setRenderKey(Math.random().toString(36).substring(2, 15));
    searchFolderByName(searchString);
  };

  const init = (data: FlatFileData<T>[]) => {
    settingsRef.current = undefined;
    library.add(faThumbtack, faThumbtackSlash);

    let mergedSettings = {
      ...defaultFileManagerSettings,
      ...props.fileManagerSettings,
    };

    flatFileSettingsRef.current = {
      ...mergedSettings,
    } as FlatFileManagerSettings<T>;

    resolveSettings(flatFileSettingsRef.current as Partial<FileManagerModel>);
    const resolvedData = resolveData(data);
    setFilesData(resolvedData);
    searchOgData.current = resolvedData;
  };

  const resolveSettings = (resolveSettings: Partial<FileManagerModel>) => {
    let newSettings = { ...resolveSettings };

    newSettings = resolveUploadSettings(newSettings);
    newSettings = resolveEvents(newSettings);

    settingsRef.current = newSettings;
  };

  const resolveUploadSettings = (settings: Partial<FileManagerModel>) => {
    if (!props.fileManagerSettings?.allowUpload) {
      // Remove Upload from toolbarSettings items
      settings.toolbarSettings = {
        ...settings.toolbarSettings,
        items: settings.toolbarSettings?.items?.filter(
          (item) => item !== "Upload"
        ),
      };

      // Disable Drag Drop
      settings.allowDragAndDrop = false;
    }
    return settings;
  };

  const fileLoadHandler = (args: FileLoadEventArgs) => {
    // Init Element
    if (args.element) {
      let fileDetails = { ...args.fileDetails } as FlatFileData<T>;
      if (fileDetails) {
        const storedFileDetails = searchOgData.current.find(
          (item) => item.id === fileDetails.id
        );
        if (storedFileDetails) {
          fileDetails = { ...storedFileDetails };
        }

        // Visibility
        const visibility =
          fileDetails.visible !== undefined
            ? `${fileDetails.visible ? "visible" : "hidden"}`
            : "";
        if (visibility) {
          args.element.classList.add(String(visibility));
        }

        // Color
        const color = fileDetails.color ? `color-${fileDetails.color}` : "";
        if (color) {
          args.element.classList.add(String(color));
        }

        // Badge
        if (fileDetails.badge) {
          const badgeElement = document.createElement("div");
          badgeElement.classList.add("ucl-flat-file-explorer-badge");
          badgeElement.innerHTML = fileDetails.badge;

          // Append inside e-text-content, sibling of e-fe-folder
          const textContent = args.element.querySelector(".e-text-content");
          if (textContent) {
            const folderElement = textContent.querySelector(".e-fe-folder");
            if (folderElement) {
              folderElement.after(badgeElement);
            }
          }
        }

        // Pinned
        if (
          flatFileSettingsRef.current?.onPinClick &&
          !isNil(fileDetails.parentId) &&
          fileDetails.canBePinned &&
          args.module === "NavigationPane"
        ) {
          const pinned = !isNil(fileDetails.isPinnedAt)
            ? "ucl-flat-file-explorer-pinned"
            : "";
          if (
            !isNil(fileDetails.isPinnedAt) &&
            !args.element.classList.contains(String(pinned))
          ) {
            args.element.classList.add(String(pinned));
          } else if (
            isNil(fileDetails.isPinnedAt) &&
            args.element.classList.contains(String(pinned))
          ) {
            args.element.classList.remove(String(pinned));
          }

          // Allow Pinned Folders
          if (!flatFileSettingsRef.current?.allowPinnedFolders) {
            return;
          }

          // Max Pinned Folders
          const currentTotalPinnedFolders = searchOgData.current.filter(
            (item) => !isNil(item.isPinnedAt)
          )?.length;

          if (
            flatFileSettingsRef.current?.maxPinnedFolders &&
            currentTotalPinnedFolders >=
              flatFileSettingsRef.current?.maxPinnedFolders &&
            isNil(fileDetails.isPinnedAt)
          ) {
            // Do not add pin element
            return;
          }

          const pinElement = document.createElement("div");
          pinElement.classList.add("ucl-flat-file-explorer-pin");
          const thumbtackIcon = icon({
            prefix: "fas",
            iconName: pinned ? "thumbtack-slash" : "thumbtack",
          });
          const iconHTML = thumbtackIcon.html.join("");
          pinElement.innerHTML = pinned ? iconHTML : iconHTML;
          pinElement.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            props.fileManagerSettings?.onPinClick?.(fileDetails);
          });

          args.element.appendChild(pinElement);
        }
      }
    }

    (props.fileManagerSettings as FileManagerModel)?.fileLoad?.(args);
  };

  const resolveEvents = (settings: FileManagerModel) => {
    settings.beforeDelete = () => 0;
    settings.beforeDownload = () => 0;
    settings.beforeFolderCreate = () => 0;
    settings.beforeImageLoad = () => 0;
    settings.beforeMove = () => 0;
    settings.beforePopupClose = () => 0;
    settings.beforePopupOpen = () => 0;
    settings.beforeRename = () => 0;
    settings.beforeSend = () => 0;
    settings.created = (args: any) => {
      // Disallow Drag Drop if allowUpload is false
      if (!props.fileManagerSettings?.allowUpload) {
        const flatFileExplorer = flatFileExplorerRef.current;
        if (flatFileExplorer) {
          flatFileExplorer.uploadObj.dropArea =
            undefined as unknown as HTMLElement;
          flatFileExplorer.uploadObj.dataBind();
        }
      }

      // New Folder Block
      if (props.fileManagerSettings?.onNewFolderClick) {
        const flatFileExplorer = flatFileExplorerRef.current;
        if (flatFileExplorer) {
          const div = document.createElement("div");
          div.classList.add(
            "ucl-flat-file-explorer-container-content-footer-new-folder-block"
          );
          flatFileExplorer.navigationpaneModule.treeObj.element.appendChild(
            div
          );
        }
      }

      if (!createdRef.current) {
        // Open the default folder
        if (props.fileManagerSettings?.defaultOpenFolderId) {
          const defaultOpenFolderId =
            props.fileManagerSettings.defaultOpenFolderId();
          if (defaultOpenFolderId) {
            flatFileExplorerRef.current?.openFile(defaultOpenFolderId);
          }
        }

        if (props.fileManagerSettings?.created) {
          props.fileManagerSettings?.created?.(args);
        }
      } else {
        if (props.fileManagerSettings?.onReset) {
          const folderId = props.fileManagerSettings?.onReset?.(args);
          if (folderId) {
            flatFileExplorerRef.current?.openFile(folderId);
          }
          return;
        }

        // Open the default folder
        if (props.fileManagerSettings?.defaultOpenFolderId) {
          const defaultOpenFolderId =
            props.fileManagerSettings.defaultOpenFolderId();
          if (defaultOpenFolderId) {
            flatFileExplorerRef.current?.openFile(defaultOpenFolderId);
          }
        }
      }

      createdRef.current = true;
    };
    settings.delete = () => 0;
    settings.destroyed = () => 0;
    settings.failure = () => 0;
    settings.fileDragStart = () => 0;
    settings.fileDragStop = () => 0;
    settings.fileDragging = () => 0;
    settings.fileDropped = () => 0;
    settings.fileLoad = (args: FileLoadEventArgs) => {
      fileLoadHandler(args);
    };
    settings.fileOpen = (args: FileOpenEventArgs) => {
      if (args.fileDetails && !(args.fileDetails as FlatFileData<T>).isFile) {
        let fileDetails = { ...args.fileDetails } as FlatFileData<T>;
        if (fileDetails) {
          const storedFileDetails = searchOgData.current.find(
            (item) => item.id === fileDetails.id
          );
          if (storedFileDetails) {
            fileDetails = { ...storedFileDetails };
          }

          // Update selected data for toolbar items when folder is opened
          if (setSelectedData) {
            const selectedDataItem =
              storedFileDetails?.data || fileDetails.data;
            setSelectedData(selectedDataItem);
          }

          // Details View Settings
          if (flatFileExplorerRef.current && flatFileSettingsRef.current) {
            if (fileDetails.detailsViewSettings) {
              flatFileExplorerRef.current.detailsViewSettings =
                fileDetails.detailsViewSettings;
            } else {
              flatFileExplorerRef.current.detailsViewSettings = (
                flatFileSettingsRef.current as FileManagerModel
              ).detailsViewSettings as DetailsViewSettingsModel;
            }
          }
        }
      }

      if ((props.fileManagerSettings as FileManagerModel)?.fileOpen) {
        (props.fileManagerSettings as FileManagerModel)?.fileOpen?.(args);
      }
    };
    settings.fileSelect = (args: FileSelectEventArgs) => {
      if ((props.fileManagerSettings as FileManagerModel)?.fileSelect) {
        (props.fileManagerSettings as FileManagerModel)?.fileSelect?.(args);
      }
    };
    settings.fileSelection = () => 0;
    settings.folderCreate = () => 0;
    settings.menuClick = () => 0;
    settings.menuClose = () => 0;
    settings.menuOpen = () => 0;
    settings.move = () => 0;
    settings.popupClose = () => 0;
    settings.popupOpen = () => 0;
    settings.rename = () => 0;
    settings.search = () => 0;
    settings.success = () => 0;
    settings.toolbarClick = () => 0;
    settings.toolbarCreate = () => 0;
    settings.uploadListCreate = () => 0;
    return settings as FileManagerModel;
  };

  // Designed by Prado, Coded by  AI
  const resolveData = (
    data: FlatFileData<T>[] | undefined
  ): { [key: string]: Object }[] => {
    if (!data) {
      return [];
    }

    let resolvedData = data;

    // Resolve Pinned Items
    if (flatFileSettingsRef.current?.onPinClick) {
      resolvedData = resolvePinnedItems(data);
    }

    return resolvedData as { [key: string]: Object }[];
  };

  const resolvePinnedItems = (
    data: FlatFileData<T>[] | undefined
  ): FlatFileData<T>[] => {
    if (!data) {
      return [];
    }

    const storedFileDetails = (flatFileSettingsRef.current as FileManagerModel)
      .fileSystemData;
    let workingData = data;
    if (storedFileDetails) {
      workingData = [...storedFileDetails];
    }

    // If a folder is pinned, it should be moved to the top of the list, excluding the root folder
    const rootFolder = workingData.find((item) =>
      isNil(item.parentId)
    ) as FlatFileData<T>;

    // Get Pinned Items whose Parent Id is the root folder
    const rootPinnedItems = workingData
      .filter(
        (item) => !isNil(item.isPinnedAt) && item.parentId === rootFolder?.id
      )
      .map((item) => ({
        ...item,
        _pinnedData: {
          originalParentId: item.parentId,
          originalParentFilterPath: workingData.find(
            (p) => String(p.id) === String(item.parentId)
          )?.filterPath,
        },
      }));

    // Get Pinned Items whose Parent Id is NOT the root folder
    const nonRootPinnedItems = workingData
      .filter(
        (item) =>
          !isNil(item.isPinnedAt) &&
          item.parentId !== rootFolder?.id &&
          item.id !== rootFolder?.id
      )
      .map((item) => ({
        ...item,
        _pinnedData: {
          originalParentId: item.parentId,
          originalParentFilterPath: workingData.find(
            (p) => String(p.id) === String(item.parentId)
          )?.filterPath,
        },
      }));

    // Create a duplicate folder of all nonRootPinnedItems with parentid of the root folder
    const nonRootPinnedItemsDuplicate = nonRootPinnedItems.map((item) => ({
      ...item,
      parentId: rootFolder?.id,
    }));

    // Get nonPinnedItems that are not pinned or are pinned but not in the root folder
    const nonPinnedItems = workingData.filter(
      (item) =>
        (isNil(item.isPinnedAt) ||
          (!isNil(item.isPinnedAt) && item.parentId !== rootFolder?.id)) &&
        item.id !== rootFolder?.id
    );

    const pinnedItems = orderBy(
      [...rootPinnedItems, ...nonRootPinnedItemsDuplicate],
      [
        (item) => {
          if (item.isPinnedAt) {
            const date = new Date(item.isPinnedAt as string);
            return isNaN(date.getTime()) ? 0 : date.getTime();
          }
          return 0;
        },
      ],
      "asc"
    );
    // Merge the rootPinnedItemsDuplicate, nonRootPinnedItemsDuplicate, and the nonPinnedItems
    let mergedData = [...pinnedItems, ...nonPinnedItems];

    // Push root folder to the top of the list
    mergedData.unshift(rootFolder);

    return mergedData;
  };

  return { ready, settingsRef, flatFileExplorerRef, fieldKey, searchFolder };
};
